import * as React from "react";
import { Checkbox } from "../checkbox";
import { cn } from "../utils";

export interface CheckboxFieldProps
  extends Omit<React.ComponentProps<typeof Checkbox>, "children"> {
  /** Primary label rendered next to the checkbox. Accepts a node so call
   *  sites can compose inline badges (e.g. a "profile" chip). */
  label: React.ReactNode;
  /** Optional muted helper line under the label. */
  description?: React.ReactNode;
}

/**
 * A checkbox with an aligned label + optional description, wrapped in a
 * `<label>` so the whole row is clickable. The shared shape behind the
 * autopilot + auto-verify settings rows — see CLAUDE.md "Frontend
 * conventions" (blocks compose atoms; never rewrite them).
 *
 * The `className` lands on the Checkbox atom (merged after the default
 * `mt-1` top-align), so callers can still tweak the control directly.
 */
export function CheckboxField({
  label,
  description,
  className,
  ...rest
}: CheckboxFieldProps) {
  return (
    <label className="flex items-start gap-3">
      <Checkbox className={cn("mt-1", className)} {...rest} />
      <span className="flex flex-col gap-0.5">
        <span className="type-body text-foreground">{label}</span>
        {description ? (
          <span className="type-body text-muted-foreground">{description}</span>
        ) : null}
      </span>
    </label>
  );
}
